import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCMSGlobal } from "../../../hooks/useCMSField";
import { useAuth } from "../../../context/AuthContext";

interface NavbarProps {
  onBookNow: () => void;
}

const DEFAULT_NAV = [
  { label: "Services",  href: "/#services"  },
  { label: "Locations", href: "/locations"  },
  { label: "About",     href: "/about"      },
  { label: "News",      href: "/news"       },
  { label: "Contact",   href: "/contact"    },
];

export default function Navbar({ onBookNow }: NavbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const globalCMS = useCMSGlobal();
  const { user } = useAuth();

  const phone    = globalCMS?.phone    ?? "03335 775445";
  const navLinks = globalCMS?.nav_links?.length ? globalCMS.nav_links : DEFAULT_NAV;

  const isActive = (href: string) =>
    href === "/" ? location.pathname === "/" : location.pathname.startsWith(href.split("#")[0]);

  return (
    <header className="bg-white border-b-4 border-cl-dark sticky top-0 z-50">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between gap-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-1 flex-shrink-0 cursor-pointer">
          <span className="text-2xl font-black tracking-tight text-cl-dark">
            Clear<span className="text-cl-green">io</span>
          </span>
          <span className="w-2.5 h-2.5 rounded-full bg-cl-green mb-4 inline-block border-2 border-cl-dark"></span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
                isActive(link.href)
                  ? "bg-cl-lime text-cl-dark"
                  : "text-cl-dark hover:bg-cl-tint hover:text-cl-green"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right actions */}
        <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
          <a
            href={`tel:${phone.replace(/\s/g, "")}`}
            className="flex items-center gap-1.5 text-sm font-bold text-cl-dark hover:text-cl-green transition-colors"
          >
            <i className="ri-phone-fill text-cl-green text-sm"></i>
            {phone}
          </a>
          <Link
            to="/account"
            className="flex items-center gap-1.5 text-sm font-bold text-cl-dark hover:text-cl-green transition-colors"
          >
            <i className="ri-user-3-line text-sm"></i>
            {user ? user.first_name : "Account"}
          </Link>
          <button
            onClick={onBookNow}
            className="bg-cl-green hover:bg-green-700 text-white font-black px-5 py-2.5 rounded-xl text-sm transition-colors cartoon-btn-green"
          >
            Book Now
          </button>
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden p-2 rounded-xl border-2 border-cl-dark"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-cl-dark text-xl`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t-2 border-cl-dark px-6 py-4 flex flex-col gap-2">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={() => setMenuOpen(false)}
              className={`px-4 py-3 rounded-xl text-sm font-bold transition-colors ${
                isActive(link.href) ? "bg-cl-lime text-cl-dark" : "text-cl-dark hover:bg-cl-tint"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/account" onClick={() => setMenuOpen(false)} className="px-4 py-3 rounded-xl text-sm font-bold text-cl-dark hover:bg-cl-tint">
            {user ? `My Account (${user.first_name})` : "Account"}
          </Link>
          <a href={`tel:${phone.replace(/\s/g, "")}`} className="px-4 py-3 rounded-xl text-sm font-bold text-cl-dark hover:bg-cl-tint flex items-center gap-2">
            <i className="ri-phone-fill text-cl-green"></i>{phone}
          </a>
          <button
            onClick={() => { setMenuOpen(false); onBookNow(); }}
            className="bg-cl-green text-white font-black px-5 py-3 rounded-xl text-sm cartoon-btn-green mt-1"
          >
            Book Now
          </button>
        </div>
      )}
    </header>
  );
}
